import colorVars from "@/assets/styles/colorVars"
import { AnimationPlayer } from "@/components/animation/use-cases/AnimationPlayer"
import { animationData } from "@/components/animation/use-cases/animationData"
import { CustomBackground, blurProps } from "@/components/bg/blur-bg"
import Heading from "@/components/heading"
import { Tabs, TabsItemProps } from "@/components/tabs"
import useIntersectionObserver from "@react-hook/intersection-observer"
import React, { FC, useEffect, useRef, useState } from "react"
import { up } from "styled-breakpoints"
import styled from "styled-components"

export interface IUseCasesTab extends TabsItemProps {
  video: Queries.WpMediaItem
}

interface IUseCases {
  anchor?: string
  caption?: string
  tabs: Array<IUseCasesTab>
  title: string
}

export interface UseCasesProps {
  className?: string
  data?: IUseCases
}

interface IActiveProp {
  active?: boolean
}

const Container = styled.div<UseCasesProps>`
  display: block;
  width: 100%;
  margin: 0 auto;
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 47px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 16px;
  margin: 0 auto;

  ${up("lg")} {
    flex-direction: row;
    gap: 45px;
    max-width: calc(1120px + 32px);
  }
`

const TabsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 1.2;
  gap: 40px 0;
  max-width: 100%;
  text-align: center;

  ${up("lg")} {
    margin-top: -18px;
    text-align: left;
  }
`

const StyledHeading = styled(Heading)`
  text-align: center;

  ${up("lg")} {
    text-align: left;
    letter-spacing: -0.8px;
  }
`

const AnimationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0.75;
  width: calc(100% + 32px);
  max-width: 660px;
  margin: 0 -16px;

  ${up("md")} {
    width: 100%;
    margin: 0;
  }

  ${up("lg")} {
    position: relative;
    top: -9px;
    left: 48px;
  }
`

const AnimationWrapper = styled.div<IActiveProp>`
  position: relative;
  width: 100%;
  max-width: 660px;
  height: 100%;
  min-height: 660px;
  aspect-ratio: 1 / 1;
`

const Caption = styled.div`
  display: flex;
  gap: 22px;
  align-items: flex-start;
  justify-content: center;
  width: calc(100% - 32px);
  max-width: 480px;
  padding: 12px 16px;
  margin: 43px auto 0;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px; /* 138.462% */
  color: ${colorVars.text.gray};
  text-align: center;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  border-radius: 8px;

  ${up("md")} {
    width: 100%;
  }

  ${up("lg")} {
    margin: 36px auto 0;
  }
`

const StyledCustomBackground = styled(CustomBackground)`
  position: relative;
  display: none;

  ${up("lg")} {
    display: block;
  }
`

const useCasesBlurList: Array<blurProps> = [
  {
    blur: "80px",
    color: colorVars.violet.p11,
    opacity: 0.4,
    pos: {
      bottom: "initial",
      left: "10%",
      right: "initial",
      top: "60%",
    },
    size: "80px",
  },
]

export const UseCases: FC<UseCasesProps> = (props) => {
  const { className, data } = props

  if (!data) return null

  const { anchor, caption, tabs, title } = data

  const containerRef: any = useRef()
  const { isIntersecting } = useIntersectionObserver(containerRef)

  const [activeTab, setActiveTab] = useState(0)

  // TIMER FUNCTION
  const ANIMATION_TIMEOUT_DELAY = 666
  const [shouldRender, setShouldRender] = useState(true)
  let nextAnimationTimer: any
  let tabAnimationTimer: any

  useEffect(() => {
    // Go to next animation
    const handleNextAnimation = () => {
      setShouldRender(false)

      const total = tabs.length - 1
      const curr = activeTab

      nextAnimationTimer = setTimeout(() => {
        if (curr === total) {
          setActiveTab(0)
        } else {
          setActiveTab(curr + 1)
        }
        setShouldRender(true)
      }, ANIMATION_TIMEOUT_DELAY)
    }

    // Listen to animations ending
    window.addEventListener("pxmTriggerNextAnimation", handleNextAnimation)

    // Cleanup
    return () => {
      // Remove event listener
      window.removeEventListener("pxmTriggerNextAnimation", handleNextAnimation)

      // Clear timers
      clearTimeout(nextAnimationTimer)
      clearTimeout(tabAnimationTimer)
    }
  }, [activeTab, isIntersecting])

  const handleActiveTab = (tab: number) => {
    setShouldRender(false)
    tabAnimationTimer = setTimeout(() => {
      setActiveTab(tab)
      setShouldRender(true)
    }, ANIMATION_TIMEOUT_DELAY)
  }

  return (
    <>
      <Container
        className={className && className !== "" ? className : ""}
        id={anchor ?? "use_cases"}
        ref={containerRef}>
        <StyledCustomBackground blurs={useCasesBlurList} />
        <Wrapper>
          <TabsWrapper>
            <StyledHeading
              size="h2"
              title={title}
              titleTag="h2"
              whiteGradient
            />
            <Tabs
              activeTab={activeTab}
              data={tabs}
              handleActiveTab={(tab: number) => handleActiveTab(tab)}
            />
          </TabsWrapper>

          <AnimationsWrapper>
            <AnimationWrapper>
              {shouldRender && isIntersecting && (
                <AnimationPlayer
                  animationData={animationData[activeTab]}
                  key={activeTab}
                />
              )}
            </AnimationWrapper>
            {caption && caption !== "" && <Caption>{caption}</Caption>}
          </AnimationsWrapper>
        </Wrapper>
      </Container>
    </>
  )
}
